import type { createPostMessageBridge } from '../../post-message-api';
import type { CSMWidgetEgressMessage, CSMWidgetIngressMessage } from '../../sdk/widget/types';
import type { AuthProvider } from '@atlassian/conversation-assistant-widget/types';

type CSMWidgetIframeMessages = {
	subscribable: CSMWidgetIngressMessage;
	sendable: CSMWidgetEgressMessage;
};

type PostMessageApi = {
	on: ReturnType<typeof createPostMessageBridge<CSMWidgetIframeMessages>>['on'];
	send: ReturnType<typeof createPostMessageBridge<CSMWidgetIframeMessages>>['send'];
};

export class OauthAuthProvider implements AuthProvider {
	readonly type = 'oauth';

	private accessToken: string | null = null;
	private pendingFetch: Promise<string> | null = null;

	constructor(
		private api: PostMessageApi,
		accessToken: string,
	) {
		this.accessToken = accessToken;
	}
	async getAuthHeaders(): Promise<Headers> {
		if (!this.accessToken) {
			if (!this.pendingFetch) {
				// Only the FIRST call creates the promise
				this.pendingFetch = this.fetchAccessToken();
			}

			// ALL concurrent calls await the SAME promise
			try {
				this.accessToken = await this.pendingFetch;
			} finally {
				this.pendingFetch = null;
			}
		}

		return new Headers({
			Authorization: `Bearer ${this.accessToken}`,
		});
	}

	private async fetchAccessToken() {
		return new Promise<string>((resolve, reject) => {
			let unsubscribe: () => void;

			const timeout = setTimeout(() => {
				reject(new Error('Timeout waiting for access token'));
				unsubscribe();
			}, 10000);

			unsubscribe = this.api.on('oauth-access-token-response', (data) => {
				clearTimeout(timeout);
				unsubscribe();

				if ('message' in data) {
					reject(new Error(data.message));
					return;
				}

				resolve(data.accessToken);
			});

			this.api.send('oauth-access-token-request');
		});
	}

	invalidate(): void {
		this.accessToken = null;
	}

	serialise(): {
		readonly type: 'oauth';
		readonly accessToken: string | null;
	} {
		return {
			type: this.type,
			accessToken: this.accessToken,
		} as const;
	}
}
